import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { MenuItemApiDto, ModuleSelectorApiDto } from '../models/menu.model';
import { EMPTY_NAV_TREE, NavTree } from '../models/nav.model';
import { buildNavTree } from './nav-tree.builder';
import { ApiResponse } from '../models/api.model';
import { environment } from '../../../environments/environment';

/**
 * Loads the navigation tree once per session and exposes it as signals.
 *
 * Replaces the old two-step flow (module list, then one `?moduleId=` call per module switch).
 * `GET /api/menu` with moduleId OMITTED already returns the whole module -> section -> page
 * forest with per-node permissions; it was simply never called that way.
 *
 * Both calls are required and neither degrades: `isEnabled` lives only on /menu/modules and is
 * what splits live from locked. Losing it would put all 30 modules — including the 19 locked
 * ones — into the rail, which is worse than an honest error state.
 */
@Injectable({ providedIn: 'root' })
export class MenuService {
  private http = inject(HttpClient);

  private _navTree = signal<NavTree | null>(null);
  private _loading = signal<boolean>(false);
  private _error   = signal<string | null>(null);

  readonly navTree = this._navTree.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error   = this._error.asReadonly();

  /** Never null — lets templates read `tree().live` without a guard on every binding. */
  readonly tree = computed<NavTree>(() => this._navTree() ?? EMPTY_NAV_TREE);
  readonly liveModules   = computed(() => this.tree().live);
  readonly lockedModules = computed(() => this.tree().locked);
  readonly nodesById     = computed(() => this.tree().byId);

  /** True once a load has completed — lets pages skip rendering a wrongly-disabled toolbar. */
  readonly ready = computed(() => this._navTree() !== null);

  constructor() {
    // The unified sidebar derives the active module from the URL on every navigation, so the
    // persisted module is redundant. It was also the source of the "MOD_BOOKING · Calendar"
    // breadcrumb — a module CODE where a NAME was expected. Clear it from existing sessions.
    try { localStorage.removeItem('pss_active_module'); } catch { /* private mode */ }
  }

  /**
   * Fetches both menu endpoints in parallel and builds the tree.
   * Safe to call repeatedly; callers should check `ready()` first.
   */
  loadNav(): Observable<NavTree> {
    this._loading.set(true);
    this._error.set(null);

    return forkJoin({
      modules: this.http.get<ApiResponse<ModuleSelectorApiDto[]>>(`${environment.apiUrl}/menu/modules`),
      tree:    this.http.get<ApiResponse<MenuItemApiDto[]>>(`${environment.apiUrl}/menu`),
      paths:   this.http.get<ApiResponse<string[]>>(`${environment.apiUrl}/menu/paths`),
    }).pipe(
      map(({ modules, tree, paths }) => {
        if (!modules?.success || !tree?.success) {
          throw new Error(modules?.message || tree?.message || 'Navigation request was not successful.');
        }
        return buildNavTree(modules.data ?? [], tree.data ?? [], paths?.data ?? [],
          { warn: !environment.production });
      }),
      tap(t => this._navTree.set(t)),
      catchError(err => {
        this._error.set(
          err?.error?.message ?? err?.message ?? 'Could not load navigation.',
        );
        return of(EMPTY_NAV_TREE);
      }),
      finalize(() => this._loading.set(false)),
    );
  }

  /**
   * Drops the cached tree. MenuService is a root singleton, so without this the previous
   * user's tree (e.g. an admin's) survives logout/login in the same tab and the next user
   * briefly sees modules they don't hold until a hard refresh reloads it.
   */
  reset(): void { this._navTree.set(null); this._error.set(null); }

  /** Retry entry point for the sidebar's error state. */
  reloadNav(): void {
    this._navTree.set(null);
    this.loadNav().subscribe();
  }
}
